/**
 * 키오스크 오류 메시지 정제·중복 억제·크래시 정책 (BOOTH-97) — 순수 함수 (vitest).
 * 부수효과(큐·전송)는 `activity.ts`, 전역 핸들러는 `globalErrors.ts`.
 *
 * BOOTH-109: 정제 규칙은 스키마 `sanitizeErrorText` 로 옮겨 서버·콘솔과 공유한다. 여기엔 키오스크 쪽 조립
 * (`resolveErrorMessage`·`errorStack`·`unpackApiError`·`buildErrorDetail`) 만 남긴다.
 */
import {
  ERROR_DETAIL_BODY_MAX,
  ERROR_DETAIL_CODE_MAX,
  ERROR_DETAIL_URL_MAX,
  ERROR_MESSAGE_MAX,
  sanitizeErrorText,
  sanitizeStackText,
  type KioskErrorDetail,
} from "@fiket/booth-schema";

/** 기본 중복 억제 창 — 같은 오류가 이 시간 안에 반복되면 한 번만 보낸다 */
const DEDUPE_WINDOW_MS = 10_000;

/**
 * 임의의 throw 값을 서버에 보낼 한 줄 메시지로 바꾼다.
 * Error 는 `이름: 메시지`(이름이 "Error" 면 메시지만), 문자열은 그대로, 객체는 JSON, 그 외는 `String()`.
 * dataURL·base64 본문·API 키를 지우고 공백을 한 칸으로 모은 뒤 `max` 자로 자른다 (`sanitizeErrorText`).
 */
export function errorMessage(err: unknown, max: number = ERROR_MESSAGE_MAX): string {
  return sanitizeErrorText(err, max);
}

/**
 * 전송할 최종 메시지 — 호출자가 조립한 문구(`fieldsMessage`)가 있으면 그것, 없으면 `err` 를 정제한다.
 * **어느 경로든 반드시 `errorMessage` 를 한 번 통과한다** — 서버도 BOOTH-109 부터는 절단하지만(거부 아님), 키오스크가
 * 먼저 정제해야 손님 사진 dataURL 이 네트워크를 타지 않는다. `errorMessage` 는 멱등이라 두 번 통과해도 안전하다.
 */
export function resolveErrorMessage(err: unknown, fieldsMessage?: string): string | undefined {
  if (fieldsMessage !== undefined) return errorMessage(fieldsMessage);
  if (err === undefined) return undefined;
  return errorMessage(err);
}

/** `Error.stack` 을 정제해 돌려준다 (≤ ERROR_DETAIL_BODY_MAX). Error 가 아니거나 스택이 없으면 undefined. */
export function errorStack(err: unknown): string | undefined {
  if (!(err instanceof Error) || !err.stack) return undefined;
  const s = sanitizeStackText(err.stack, ERROR_DETAIL_BODY_MAX);
  return s || undefined;
}

/**
 * 우리 API 오류 클래스(`ApiEnvelopeError`·`SessionApiError`·`PrintApiError`) 를 덕타이핑으로 푼다 — `{ code, httpStatus }`.
 * feature 모듈을 import 하지 않아 텔레메트리가 순환 의존 없이 어느 오류든 받을 수 있다.
 */
export function unpackApiError(err: unknown): { serverCode?: string; httpStatus?: number } {
  if (!err || typeof err !== "object") return {};
  const o = err as { code?: unknown; httpStatus?: unknown };
  const out: { serverCode?: string; httpStatus?: number } = {};
  if (typeof o.code === "string" && o.code) out.serverCode = o.code;
  if (typeof o.httpStatus === "number" && Number.isInteger(o.httpStatus) && o.httpStatus >= 100 && o.httpStatus <= 599) {
    out.httpStatus = o.httpStatus;
  }
  return out;
}

/** `trackError` 가 받는 부가 필드 (BOOTH-109). 명시값이 `err` 에서 푼 값보다 우선한다. */
export interface TrackErrorFields {
  message?: string;
  httpStatus?: number;
  serverCode?: string;
  url?: string;
  /** `err.stack` 대신 보낼 스택 텍스트 (ErrorBoundary 의 componentStack 등) */
  stackText?: string;
}

/**
 * `client_error.detail` 조립 — `err` 의 code/httpStatus/stack + 명시 필드. 쓸 키가 없으면 undefined.
 * 길이는 서버가 다시 절단하지만 여기서도 상한을 지켜 배치 페이로드를 키우지 않는다.
 */
export function buildErrorDetail(err: unknown, fields: TrackErrorFields = {}): KioskErrorDetail | undefined {
  const unpacked = unpackApiError(err);
  const out: KioskErrorDetail = {};
  const httpStatus = fields.httpStatus ?? unpacked.httpStatus;
  if (httpStatus !== undefined) out.httpStatus = httpStatus;
  const serverCode = fields.serverCode ?? unpacked.serverCode;
  if (serverCode) out.serverCode = sanitizeErrorText(serverCode, ERROR_DETAIL_CODE_MAX);
  if (fields.url) out.url = sanitizeErrorText(fields.url, ERROR_DETAIL_URL_MAX);
  const stack = fields.stackText !== undefined ? sanitizeStackText(fields.stackText, ERROR_DETAIL_BODY_MAX) : errorStack(err);
  if (stack) out.stack = stack;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** 같은 key 가 `windowMs` 안에 다시 왔는지 — 참이면 전송을 건너뛴다. */
export function shouldDedupe(
  prev: { key: string; at: number } | null,
  key: string,
  now: number,
  windowMs: number = DEDUPE_WINDOW_MS
): boolean {
  return prev !== null && prev.key === key && now - prev.at < windowMs;
}

/**
 * 렌더 크래시 복구 방법 (Review Focus 5) — 한 방문의 첫 크래시는 세션 리셋으로 수습하고,
 * 같은 방문에서 또 깨지면 번들·상태가 망가진 것으로 보고 페이지를 새로고침한다.
 */
export function crashPolicy(crashesInVisit: number): "reset" | "reload" {
  return crashesInVisit >= 2 ? "reload" : "reset";
}
